import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { ArrowUpRight, Filter } from 'lucide-react';

import type { DealStage } from '../../features/deals/api/deals.client.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

export interface PipelineBoardCard {
  id: string;
  title: string;
  contact: string | null;
  amount: number;
  currency: string;
  stage: DealStage;
  nextActivityDue: string | null;
}

export interface PipelineBoardWidgetProps {
  cards?: readonly PipelineBoardCard[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onOpenDeal?: (dealId: string) => void;
  onOpenKanban?: () => void;
}

/** Column limit per stage group — the funnel is a digest, not the kanban. */
const COLUMN_CARD_LIMIT = 3;

type ColumnKey = 'new' | 'progress' | 'payment';

const COLUMN_STAGES: Record<ColumnKey, readonly DealStage[]> = {
  new: ['New'],
  progress: ['Contacted', 'Negotiation'],
  // Model mapping (fixed, no separate payment stage exists): won deals are
  // the salon's delivered work awaiting payment.
  payment: ['Won'],
};

function columnOf(stage: DealStage): ColumnKey {
  if (COLUMN_STAGES.new.includes(stage)) return 'new';
  if (COLUMN_STAGES.progress.includes(stage)) return 'progress';
  return 'payment';
}

function formatAmount(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatHint(
  nextActivityDue: string | null,
  locale: string,
  todayLabel: string,
  overdueLabel: string,
): string {
  if (nextActivityDue === null) return '';
  const due = new Date(nextActivityDue);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOnly = new Date(due);
  dayOnly.setHours(0, 0, 0, 0);
  if (dayOnly.getTime() === today.getTime()) return todayLabel;
  if (dayOnly.getTime() < today.getTime()) return overdueLabel;
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'short',
  }).format(due);
}

/**
 * «Воронка сделок» — mini-kanban digest (spec D `.pipe-cols`): three hairline
 * columns (Новые / В работе / К оплате), up to three fresh cards each, header
 * carrying the open-funnel size and a link to the full kanban.
 */
export function PipelineBoardWidget({
  cards = [],
  className,
  testId,
  loading = false,
  onOpenDeal,
  onOpenKanban,
}: PipelineBoardWidgetProps): ReactElement {
  const { t, locale } = useTranslation();
  const canOpenKanban = typeof onOpenKanban === 'function';

  const columns: { key: ColumnKey; label: string; cards: PipelineBoardCard[] }[] = (
    ['new', 'progress', 'payment'] as const
  ).map((key) => ({
    key,
    label: t(`crm.dashboard.pipeline_board.column.${key}`),
    cards: cards.filter((card) => columnOf(card.stage) === key).slice(0, COLUMN_CARD_LIMIT),
  }));

  // Open funnel: everything that has not closed yet (Lost excluded upstream).
  const openAmount = cards
    .filter((card) => card.stage !== 'Won')
    .reduce((sum, card) => sum + card.amount, 0);
  const openCount = cards.filter((card) => card.stage !== 'Won').length;
  const openCurrencies = new Set(
    cards.filter((card) => card.stage !== 'Won').map((card) => card.currency),
  );
  const openTotal =
    openCurrencies.size === 1
      ? formatAmount(openAmount, [...openCurrencies][0] ?? 'RUB', locale)
      : `${new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU').format(openAmount)}`;

  const widgetContainerProps = {
    title: t('crm.dashboard.pipeline_board.title'),
    icon: Filter,
    iconColor: 'var(--ant-color-primary-border-hover, #d97706)',
    minHeight: 200,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span className="flex items-center gap-2">
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 550,
            whiteSpace: 'nowrap',
            color: 'var(--ant-color-primary-text, #b45309)',
            border:
              '1px solid color-mix(in srgb, var(--ant-color-primary-border-hover, #d97706) 45%, transparent)',
            background: 'color-mix(in srgb, var(--ant-color-primary-bg, #fffbeb) 80%, transparent)',
            borderRadius: 999,
            padding: '0 8px',
            lineHeight: '17px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {openCount} · {openTotal}
        </span>
        <button
          type="button"
          {...(canOpenKanban ? { onClick: onOpenKanban } : {})}
          disabled={!canOpenKanban}
          className="flex items-center gap-1 font-medium"
          style={{
            fontSize: 11,
            color: canOpenKanban
              ? 'var(--ant-color-primary-text, #b45309)'
              : 'var(--ant-color-text-tertiary, #6d6963)',
            cursor: canOpenKanban ? 'pointer' : 'default',
            transition: 'opacity 110ms ease',
          }}
        >
          {t('crm.dashboard.pipeline_board.kanban_link')} <ArrowUpRight size={10} />
        </button>
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div
          className="animate-pulse"
          style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              style={{
                height: 42,
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
                background: 'var(--g-fill, #f5f1eb)',
              }}
            />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <Filter size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.pipeline_board.empty')}
          </span>
        </div>
      ) : (
        <div className="crm-pipe-cols">
          {columns.map((column) => (
            <div key={column.key} className="crm-pipe-col">
              <div className="flex items-center gap-1.5" style={{ padding: '9px 12px' }}>
                <span
                  className="uppercase"
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.07em',
                    color: 'var(--ant-color-text-secondary, #57534e)',
                  }}
                >
                  {column.label}
                </span>
                <span
                  style={{
                    flex: 1,
                    height: 1,
                    background: 'var(--ant-color-border-secondary, #e7e5e4)',
                  }}
                />
                <span
                  style={{
                    fontSize: 10.5,
                    color: 'var(--ant-color-text-tertiary, #6d6963)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cards.filter((card) => columnOf(card.stage) === column.key).length}
                </span>
              </div>
              <div
                style={{ padding: '0 8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                {column.cards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    {...(onOpenDeal ? { onClick: () => onOpenDeal(card.id) } : {})}
                    disabled={!onOpenDeal}
                    className="crm-hover-fill block w-full border bg-transparent text-left"
                    style={{
                      border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
                      borderRadius: 'var(--ant-border-radius-sm, 6px)',
                      padding: '8px 10px',
                      cursor: onOpenDeal ? 'pointer' : 'default',
                      transition: 'background-color 110ms ease, border-color 110ms ease',
                    }}
                  >
                    <span
                      className="block truncate"
                      style={{
                        fontSize: 12.5,
                        fontWeight: 550,
                        color: 'var(--ant-color-text, #292524)',
                      }}
                    >
                      {card.title}
                    </span>
                    {card.contact ? (
                      <span
                        className="block truncate"
                        style={{
                          fontSize: 11.5,
                          color: 'var(--ant-color-text-tertiary, #6d6963)',
                          marginTop: 1,
                        }}
                      >
                        {card.contact}
                      </span>
                    ) : null}
                    <span className="flex items-center gap-2" style={{ marginTop: 6 }}>
                      <span
                        style={{
                          fontFamily:
                            'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                          fontSize: 11.5,
                          fontWeight: 550,
                          color: 'var(--ant-color-text, #292524)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatAmount(card.amount, card.currency, locale)}
                      </span>
                      {card.nextActivityDue ? (
                        <span
                          className="ml-auto truncate"
                          style={{
                            fontSize: 10,
                            color:
                              new Date(card.nextActivityDue).getTime() < Date.now()
                                ? 'var(--ant-color-error, #b91c1c)'
                                : 'var(--ant-color-text-tertiary, #6d6963)',
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                          }}
                        >
                          {formatHint(
                            card.nextActivityDue,
                            locale,
                            t('crm.dashboard.pipeline_board.hint_today'),
                            t('crm.dashboard.pipeline_board.hint_overdue'),
                          )}
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetContainer>
  );
}
